/**
 * Smart Branding - Automatisches Branding durch Logo-Analyse
 * 
 * Features:
 * - Logo-Upload mit automatischer Farbextraktion
 * - Vorschau der extrahierten Farben
 * - Branding wird automatisch auf UI, Rechnungen, E-Mails angewendet
 */

import React, { useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { 
  Upload, Palette, Check, Loader2, 
  Image as ImageIcon, FileText, Mail, Monitor
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SmartBranding() {
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [branding, setBranding] = useState(null);
  
  const sessionToken = localStorage.getItem('access_token') || localStorage.getItem('access_token');

  // Load existing branding
  React.useEffect(() => {
    const loadBranding = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/branding/profile`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
        if (response.ok) {
          const data = await response.json();
          setBranding(data);
          if (data.logo_url) {
            setLogoPreview(`${BACKEND_URL}${data.logo_url}`);
          }
        }
      } catch (error) {
        console.error('Error loading branding:', error);
      }
    };
    if (sessionToken) loadBranding();
  }, [sessionToken]);

  // Handle file selection
  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte ein Bild hochladen');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo zu groß (max 5MB)');
      return;
    }
    
    setLogo(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
  }, []);

  // Upload logo
  const uploadLogo = async () => {
    if (!logo) return;
    
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo_file', logo);
      
      const response = await fetch(`${BACKEND_URL}/api/branding/upload-logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}` },
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload fehlgeschlagen');
      
      const data = await response.json();
      setBranding({ colors: data.colors, palette: data.palette, logo_url: data.logo_url });
      toast.success('🎨 Branding automatisch erstellt!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl">
            <Palette className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Smart Branding</h1>
            <p className="text-slate-400">Logo hochladen → Farben automatisch extrahieren</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Upload Section */}
          <Card className="bg-slate-800 border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Logo hochladen</h2>
            
            <div 
              className={`
                border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                ${logoPreview ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-600 hover:border-slate-500'}
              `}
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="logo-upload"
              />
              <label htmlFor="logo-upload" className="cursor-pointer">
                {logoPreview ? (
                  <div className="space-y-4">
                    <img 
                      src={logoPreview} 
                      alt="Logo" 
                      className="max-h-32 mx-auto rounded-lg"
                    />
                    <p className="text-slate-400 text-sm">Klicken zum Ändern</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto">
                      <Upload className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-white">Logo hier hochladen</p>
                    <p className="text-sm text-slate-400">PNG, JPG, WebP (max 5MB)</p>
                  </div>
                )}
              </label>
            </div>
            
            {logo && (
              <Button
                onClick={uploadLogo}
                disabled={isUploading}
                className="w-full mt-4 bg-gradient-to-r from-purple-500 to-pink-500"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Farben werden extrahiert...
                  </>
                ) : (
                  <>
                    <Palette className="w-4 h-4 mr-2" />
                    Branding erstellen
                  </>
                )}
              </Button>
            )}
          </Card>

          {/* Color Preview */}
          <Card className="bg-slate-800 border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Extrahierte Farben</h2>
            
            {branding?.colors ? (
              <div className="space-y-4">
                {/* Main Colors */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div 
                      className="w-full h-16 rounded-xl mb-2"
                      style={{ backgroundColor: branding.colors.primary }}
                    />
                    <p className="text-xs text-slate-400">Primär</p>
                    <p className="text-xs text-white">{branding.colors.primary}</p>
                  </div>
                  <div className="text-center">
                    <div 
                      className="w-full h-16 rounded-xl mb-2"
                      style={{ backgroundColor: branding.colors.secondary }}
                    />
                    <p className="text-xs text-slate-400">Sekundär</p>
                    <p className="text-xs text-white">{branding.colors.secondary}</p>
                  </div>
                  <div className="text-center">
                    <div 
                      className="w-full h-16 rounded-xl mb-2"
                      style={{ backgroundColor: branding.colors.accent }}
                    />
                    <p className="text-xs text-slate-400">Akzent</p>
                    <p className="text-xs text-white">{branding.colors.accent}</p>
                  </div>
                </div>

                {/* Palette */}
                {branding.palette && (
                  <div>
                    <p className="text-sm text-slate-400 mb-2">Farbpalette</p>
                    <div className="flex gap-2">
                      {branding.palette.map((color, idx) => (
                        <div 
                          key={idx}
                          className="w-10 h-10 rounded-lg"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Applied To */}
                <div className="mt-6 pt-4 border-t border-slate-700">
                  <p className="text-sm text-slate-400 mb-3">Wird angewendet auf:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center gap-2 text-slate-300 text-sm">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <Monitor className="w-4 h-4" />
                      Kassensystem UI
                    </div>
                    <div className="flex items-center gap-2 text-slate-300 text-sm">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <FileText className="w-4 h-4" />
                      Rechnungen
                    </div>
                    <div className="flex items-center gap-2 text-slate-300 text-sm">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <Mail className="w-4 h-4" />
                      E-Mail-Templates
                    </div>
                    <div className="flex items-center gap-2 text-slate-300 text-sm">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <ImageIcon className="w-4 h-4" />
                      QR-Bestellseite
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-500">
                <p>Logo hochladen um Farben zu extrahieren</p>
              </div>
            )}
          </Card>
        </div>

        {/* Preview */}
        {branding?.colors && (
          <Card className="bg-slate-800 border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Vorschau: E-Mail Template</h2>
            
            <div className="bg-white rounded-xl overflow-hidden max-w-lg mx-auto">
              {/* Email Header */}
              <div 
                className="p-6 text-center"
                style={{ backgroundColor: branding.colors.primary }}
              >
                {logoPreview && (
                  <img src={logoPreview} alt="Logo" className="h-12 mx-auto mb-2" />
                )}
                <h3 className="text-white text-xl font-bold">Willkommen!</h3>
              </div>
              
              {/* Email Body */}
              <div className="p-6">
                <h4 className="text-gray-800 font-semibold mb-2">Schön, dass Sie da sind!</h4>
                <p className="text-gray-600 mb-4">
                  Vielen Dank für Ihre erste Bestellung. Wir freuen uns, Sie als Kunden begrüßen zu dürfen.
                </p>
                <button
                  className="px-6 py-2 rounded-lg text-white font-medium"
                  style={{ backgroundColor: branding.colors.primary }}
                >
                  Jetzt bestellen
                </button>
              </div>
              
              {/* Email Footer */}
              <div className="bg-gray-100 p-4 text-center text-gray-500 text-sm">
                © 2025 Ihr Restaurant. Alle Rechte vorbehalten.
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
